import { createSlice } from "@reduxjs/toolkit";

import api from "../../helpers/axios";
import { GET_GOALS, GET_GOALS_STATUS } from "../../helpers/url_helpers";
import { setSelectedGoal } from "./survey-CRR.slice";

const sortGoalQuestions = (goal) => ({
  ...goal,
  questions: [...(goal.questions || [])]
    .filter((q) => q?.id != null)
    .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0)),
});

const initialState = {
  loading: false,
  goalsData: "",
  goalsStatusData: [],
};

const goals = createSlice({
  name: "goals",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload.loading;
    },
    setGoalsData: (state, action) => {
      state.goalsData = action.payload.goalsData;
    },
    setGoalsStatusData: (state, action) => {
      state.goalsStatusData = action.payload.goalsStatusData;
    },
    setGoalsDataForColouring: (state, action) => {
      const newGoalIndex = action.payload.newGoalsData.findIndex(
        (goal) => goal.id === action.payload.goalID
      );
      const newGoalsData = [...state.goalsData];
      newGoalsData[newGoalIndex] = sortGoalQuestions(
        action.payload.newGoalsData[newGoalIndex]
      );
      state.goalsData = newGoalsData;
    },
    enrichGoalQuestionOrder: (state, action) => {
      const { goalId, questions = [] } = action.payload;
      if (!Array.isArray(state.goalsData) || !goalId) return;
      const orderById = Object.fromEntries(
        questions.filter((q) => q?.id != null).map((q) => [q.id, q.order])
      );
      state.goalsData = state.goalsData.map((goal) => {
        if (goal.id !== goalId) return goal;
        return sortGoalQuestions({
          ...goal,
          questions: (goal.questions || []).map((q) => ({
            ...q,
            order: orderById[q.id] ?? q.order,
          })),
        });
      });
    },
    setError: (state, action) => {
      state.goalsError = action.payload.goalsError;
    },
  },
});

export const {
  setLoading,
  setGoalsData,
  setGoalsStatusData,
  setGoalsDataForColouring,
  enrichGoalQuestionOrder,
  setError,
  setGoalChanged,
  setDomainChanged,
} = goals.actions;
export default goals.reducer;

export const getGoalsData =
  (domainId, surveyId = "", callback) =>
  async (dispatch) => {
    const goalsUrl = GET_GOALS.replace("{domainId}", domainId).replace(
      "{surveyId}",
      surveyId
    );
    const goalsStatusUrl = GET_GOALS_STATUS.replace(
      "{domainId}",
      domainId
    ).replace("{surveyId}", surveyId);

    try {
      dispatch(setLoading({ loading: true }));
      dispatch(setGoalsData({ goalsData: "" }));

      // Call both APIs in parallel
      const [goalsResponse, goalsStatusResponse] = await Promise.all([
        api.get(goalsUrl),
        api.get(goalsStatusUrl),
      ]);

      if (goalsResponse.data.error) {
        dispatch(setError({ goalsError: goalsResponse.data.error }));
      } else {
        const goalsData = (goalsResponse.data.data || []).map(sortGoalQuestions);
        dispatch(setGoalsData({ goalsData }));
        dispatch(setSelectedGoal(goalsData[0]));
        if (callback) {
          callback(goalsData);
        }
      }

      if (goalsStatusResponse.data.error) {
        dispatch(setError({ goalsError: goalsStatusResponse.data.error }));
      } else {
        dispatch(
          setGoalsStatusData({ goalsStatusData: goalsStatusResponse.data.data })
        );
      }
    } catch (error) {
      dispatch(
        setError({ goalsError: "Failed to fetch goals data or goals status" })
      );
    } finally {
      dispatch(setLoading({ loading: false }));
    }
  };

export const getGoalsStatusData =
  (domainId = "", surveyId = "") =>
  async (dispatch) => {
    try {
      dispatch(setLoading({ loading: true }));
      // dispatch(setGoalsData({ goalsStatusData: "" }));
      // GET Goals Status api call
      const response = await api.get(
        GET_GOALS.replace("{domainId}", domainId).replace(
          "{surveyId}",
          surveyId
        )
      );

      if (response.data.error) {
        dispatch(
          setError({
            goalsError: response.data.error,
          })
        );
      } else {
        dispatch(setGoalsStatusData({ goalsStatusData: response.data?.data }));
      }
      dispatch(setLoading({ loading: false }));
    } catch (error) {
      dispatch(setLoading({ loading: false }));
    }
  };

export const getGoalsDataForColouring =
  (domainId, surveyId = "", goalID, callback) =>
  async (dispatch) => {
    try {
      dispatch(setLoading({ loading: true }));
      // GET Goals api call
      const response = await api.get(
        GET_GOALS.replace("{domainId}", domainId).replace(
          "{surveyId}",
          surveyId
        )
      );

      if (response.data.error) {
        dispatch(
          setError({
            goalsError: response.data?.error,
          })
        );
      } else {
        dispatch(
          setGoalsDataForColouring({
            newGoalsData: response?.data?.data,
            goalID,
          })
        );
        if (callback) callback();

        // dispatch(setGoalsStatusData({ goalsStatusData: "" }));
        const goalsStatusResponse = await api.get(
          GET_GOALS_STATUS.replace("{domainId}", domainId).replace(
            "{surveyId}",
            surveyId
          )
        );

        if (response.data.error) {
          dispatch(
            setError({
              goalsError: goalsStatusResponse.data?.error,
            })
          );
        } else {
          dispatch(
            setGoalsStatusData({
              goalsStatusData: goalsStatusResponse?.data?.data,
            })
          );
        }
      }
      dispatch(setLoading({ loading: false }));
    } catch (error) {
      dispatch(setLoading({ loading: false }));
    }
  };

export const clearGoalsData = () => async (dispatch) => {
  try {
    dispatch(setGoalsData({ goalsData: "" }));
  } catch (error) {
    dispatch(setLoading({ loading: false }));
  }
};
